import React from 'react';
import { Box, Button, Typography } from '@mui/material';

/**
 * ErrorBoundary para capturar erros de renderizacao do React.
 * 
 * Este componente captura erros como "Failed to execute 'insertBefore' on 'Node'"
 * que ocorrem quando scripts externos (Google Maps, Meta Pixel, etc.) ou
 * extensoes de navegador (Google Translate, AdBlock) modificam o DOM.
 */

// Funcao auxiliar para verificar se eh erro de DOM
const isDOMNodeError = (error) => {
  const message = error?.message || '';
  return (
    message.includes('insertBefore') ||
    message.includes('removeChild') ||
    message.includes('appendChild') ||
    message.includes('replaceChild') ||
    message.includes('The node before which') ||
    message.includes('is not a child of this node') ||
    message.includes('NotFoundError') ||
    message.includes('Node cannot be inserted') ||
    message.includes('Failed to execute') && message.includes('on \'Node\'')
  );
};

// Intercepta erros globais de DOM antes que quebrem o React
if (typeof window !== 'undefined') {
  // Salva os metodos originais
  const originalInsertBefore = Node.prototype.insertBefore;
  const originalRemoveChild = Node.prototype.removeChild;
  const originalAppendChild = Node.prototype.appendChild;
  const originalReplaceChild = Node.prototype.replaceChild;

  // Wrapper seguro para insertBefore
  Node.prototype.insertBefore = function(newNode, referenceNode) {
    try {
      // Verifica se o referenceNode ainda eh filho deste node
      if (referenceNode && referenceNode.parentNode !== this) {
        // Se nao for filho, apenas adiciona ao final
        return originalAppendChild.call(this, newNode);
      }
      return originalInsertBefore.call(this, newNode, referenceNode);
    } catch (error) {
      if (isDOMNodeError(error)) {
        console.warn('[GIO] Erro de DOM interceptado em insertBefore:', error.message);
        // Tenta adicionar ao final como fallback
        try {
          return originalAppendChild.call(this, newNode);
        } catch (e) {
          // Ignora silenciosamente
          return newNode;
        }
      }
      throw error;
    }
  };

  // Wrapper seguro para removeChild
  Node.prototype.removeChild = function(child) {
    try {
      // Verifica se o child ainda eh filho deste node
      if (child && child.parentNode !== this) {
        // Se nao for filho, ignora silenciosamente
        return child;
      }
      return originalRemoveChild.call(this, child);
    } catch (error) {
      if (isDOMNodeError(error)) {
        console.warn('[GIO] Erro de DOM interceptado em removeChild:', error.message);
        // Ignora silenciosamente
        return child;
      }
      throw error;
    }
  };

  // Wrapper seguro para appendChild
  Node.prototype.appendChild = function(child) {
    try {
      return originalAppendChild.call(this, child);
    } catch (error) {
      if (isDOMNodeError(error)) {
        console.warn('[GIO] Erro de DOM interceptado em appendChild:', error.message);
        return child;
      }
      throw error;
    }
  };

  // Wrapper seguro para replaceChild
  Node.prototype.replaceChild = function(newChild, oldChild) {
    try {
      // Verifica se o oldChild ainda eh filho deste node
      if (oldChild && oldChild.parentNode !== this) {
        // Se nao for filho, apenas adiciona o novo node
        return originalAppendChild.call(this, newChild);
      }
      return originalReplaceChild.call(this, newChild, oldChild);
    } catch (error) {
      if (isDOMNodeError(error)) {
        console.warn('[GIO] Erro de DOM interceptado em replaceChild:', error.message);
        try {
          return originalAppendChild.call(this, newChild);
        } catch (e) {
          return newChild;
        }
      }
      throw error;
    }
  };
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, retryCount: 0, isNodeError: false };
    this.maxRetries = 5;
  }

  static getDerivedStateFromError(error) {
    // Verifica se eh o erro especifico de insertBefore/removeChild
    const isNodeError = isDOMNodeError(error);

    // Para erros de DOM, nao marca hasError imediatamente para permitir retry silencioso
    if (isNodeError) {
      return { hasError: false, error, isNodeError: true };
    }

    // Atualiza o state para que a proxima renderizacao mostre a UI de fallback
    return { hasError: true, error, isNodeError: false };
  }

  componentDidCatch(error, errorInfo) {
    // Verifica se eh o erro especifico de insertBefore/removeChild
    const isNodeError = isDOMNodeError(error);

    // Só loga se não for erro de DOM (para evitar spam no console)
    if (!isNodeError) {
      console.error('ErrorBoundary capturou um erro:', error, errorInfo);
    }
    
    this.setState({ errorInfo });

    if (isNodeError) {
      const { retryCount } = this.state;
      
      if (retryCount < this.maxRetries) {
        // Para erros de DOM, tenta recuperar automaticamente apos um breve delay
        setTimeout(() => {
          this.setState(prevState => ({ 
            hasError: false, 
            error: null, 
            errorInfo: null,
            isNodeError: false,
            retryCount: prevState.retryCount + 1
          }));
        }, 100 * (retryCount + 1)); // Delay progressivo
      } else {
        // Se excedeu o numero de retries, força reload
        console.warn('[GIO] Muitos erros de DOM. Recarregando pagina...');
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    // Recarrega a pagina
    window.location.reload();
  };

  handleRetry = () => {
    // Tenta renderizar novamente, resetando o contador de retries
    this.setState({ hasError: false, error: null, errorInfo: null, isNodeError: false, retryCount: 0 });
  };

  render() {
    // Se esta em modo de retry silencioso para erros de DOM, renderiza normalmente
    if (this.state.isNodeError && !this.state.hasError) {
      return this.props.children;
    }

    if (this.state.hasError) {
      // Verifica se eh o erro especifico de DOM
      const isNodeError = 
        this.state.error?.message?.includes('insertBefore') ||
        this.state.error?.message?.includes('removeChild') ||
        this.state.error?.message?.includes('is not a child of this node') ||
        this.state.error?.message?.includes('NotFoundError');

      // Para erros de DOM, mostra uma UI minima enquanto tenta recuperar
      if (isNodeError) {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              p: 3,
              textAlign: 'center',
              bgcolor: 'background.default'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
              Carregando...
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Se a pagina nao carregar, clique no botao abaixo.
            </Typography>
            <Button 
              variant="contained" 
              onClick={this.handleRetry}
              sx={{ mr: 1 }}
            >
              Tentar Novamente
            </Button>
          </Box>
        );
      }

      // Para outros erros, mostra uma UI de erro mais completa
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 3,
            textAlign: 'center',
            bgcolor: 'background.default'
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ color: 'error.main' }}>
            Algo deu errado
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, maxWidth: 500 }}>
            Ocorreu um erro inesperado. Por favor, tente recarregar a pagina.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={this.handleRetry}
            >
              Tentar Novamente
            </Button>
            <Button 
              variant="contained" 
              onClick={this.handleReload}
            >
              Recarregar Pagina
            </Button>
          </Box>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box sx={{ mt: 4, textAlign: 'left', maxWidth: 600 }}>
              <Typography variant="caption" component="pre" sx={{ 
                color: 'error.main',
                bgcolor: 'error.lighter',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

